// ── EVIDENCE REPOSITORIES — the two-gate onboarding flow + status surface (§0, §5) ────────────
//
// ⚠ THE ADMIN LEAVES AND COMES BACK, SO THIS PAGE HAS NO WIZARD STATE. Between gates they go and
// run a script in their own console, or create a folder and invite a collaborator — that can take
// days. Every render is driven entirely by the server's `onboarding_state`; nothing about where
// they are is held in this component. A refresh mid-flow must land exactly where they left.
//
// ⚠ AND THE PAGE NEVER COMPUTES THE NEXT STEP. The state machine lives on the server; the client
// renders the gate it is TOLD it is on. A client that advanced itself would disagree with the
// server the first time two tabs were open.

import { useState } from 'react';
import {
  FolderLock, ExternalLink, AlertTriangle, CheckCircle2, Loader2, ShieldCheck, Download, Copy,
} from 'lucide-react';
// Build-derived (grant-script.plugin.ts): the path the bundle serves the script at, and the SHA-256
// of exactly those bytes. Never a literal.
import {
  GRANT_SCRIPT_FILENAME, GRANT_SCRIPT_ASSET_PATH, GRANT_SCRIPT_SHA256,
} from 'virtual:grant-script';
import { buildGrantCommand, checkLibraryAddress } from '@/lib/grant-command';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { RequireRole } from '@/components/auth/RequireRole';
import { REPOSITORY_LABELS } from '@/api/types/evidence-repository.labels';
import { EvidenceCollectionPanel } from '@/components/compliance/EvidenceCollectionPanel';
import { ConsentStep } from '@/components/compliance/ConsentStep';
import {
  useEvidenceOnboarding, useStartOnboarding, useConfirmEnable, useSubmitScope,
  useDiscardSetup, useRepairConnection, useCheckConnectionHealth, useSupplyCredential,
  useDeleteIntegration,
} from '@/api/hooks/useEvidenceOnboarding';
import type {
  OnboardingStatus, CredentialState, GateScript,
} from '@/api/types/evidence-onboarding.types';

/**
 * ⚠ EVERY KNOWN STATE IS NAMED, AND THE UNKNOWN ONE IS NEUTRAL. A fallback must never assert
 * progress: the previous evidence panel ended its chain in "being provisioned", so any state it
 * did not enumerate told a tenant to wait for something that would never happen.
 */
function stateLabel(state: string): string {
  switch (state) {
    case 'pending_enable': return 'not started — waiting for an administrator';
    case 'pending_scope': return 'waiting for you to choose a location';
    case 'validating': return 'checking the connection';
    case 'connected': return 'connected';
    case 'validate_failed': return 'not connected — needs a change';
    default: return `in an unrecognised state (${state})`;
  }
}

export function EvidenceRepositoriesPage() {
  return (
    <RequireRole roles={['super_admin']} label="Evidence Repositories">
      <EvidenceRepositoriesBody />
    </RequireRole>
  );
}

function EvidenceRepositoriesBody() {
  const { data, isLoading } = useEvidenceOnboarding();
  const start = useStartOnboarding();
  const enable = useConfirmEnable();
  const scope = useSubmitScope();
  const discard = useDiscardSetup();
  const repair = useRepairConnection();
  const checkHealth = useCheckConnectionHealth();
  const deleteIntegration = useDeleteIntegration();

  // ⚠ Keyed by the WIRE FIELD, not by position. A gate asks for one thing or two, and mapping by
  //   position is how the site id ends up posted as the drive id.
  const [fields, setFields] = useState<Record<string, string>>({});
  // ⚠ CLIENT-SIDE ONLY, AND NEVER POSTED. It exists to build the admin's command; sending it would
  //   re-create the defect this slice removed, where a URL reached the server and was used as an id.
  const [libraryUrl, setLibraryUrl] = useState('');
  const [label, setLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Set only after a successful delete, so the admin sees it happened; cleared by the next action.
  const [notice, setNotice] = useState<string | null>(null);

  const fail = (err: unknown) => setError(
    (err as { response?: { data?: { errors?: Array<{ message?: string }> } } })
      ?.response?.data?.errors?.[0]?.message
      ?? 'Something went wrong. Nothing was changed.');

  if (isLoading) return <Skeleton className="h-64 w-full" />;
  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="evidence-repositories">
      <header className="flex items-start gap-3">
        <FolderLock className="mt-1 h-5 w-5 text-text-secondary" />
        <div>
          <h1 className="text-xl font-semibold">Evidence Repositories</h1>
          <p className="text-sm text-text-secondary">
            Where VeriGRC keeps the evidence for your controls. Your organisation owns the
            location; VeriGRC only reaches the one place you designate.
          </p>
        </div>
      </header>

      {error && (
        <p role="alert" className="flex items-start gap-2 text-sm text-danger">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{error}
        </p>
      )}

      {notice && (
        <p role="status" data-testid="delete-notice"
           className="flex items-start gap-2 rounded border border-border p-3 text-sm">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" />{notice}
        </p>
      )}

      {data.binding ? <ConnectedOrInFlight {...{ data, enable, scope, discard, repair, checkHealth, fields,
        setFields, libraryUrl, setLibraryUrl, label, setLabel, fail, setError,
        deleteIntegration, setNotice }} />
        : <AddRepository providers={data.providers} onAdd={(k) => {
          setError(null);
          setNotice(null);
          start.mutate({ repo_kind: k }, { onError: fail });
        }} pending={start.isPending} />}
    </div>
  );
}

// ── No repository yet ───────────────────────────────────────────────────────────────────────────

function AddRepository({ providers, onAdd, pending }: {
  providers: OnboardingStatus['providers'];
  onAdd: (repoKind: string) => void;
  pending: boolean;
}) {
  return (
    <section className="space-y-3" data-testid="add-repository">
      <h2 className="text-sm font-medium">Add a repository</h2>
      {/* ⚠ DEFENSE, NOT THE FIX. The api contract is the real guarantee — the Box health arm
          now returns the same statusBody the SharePoint arm does. This exists because a mutation
          response REPLACES this whole object via qc.setQueryData — no merge, no refetch — so a
          future response that omits a field would blank the entire settings page rather than
          degrade. A page must not be destroyed by one absent array. */}
      {(providers ?? []).map((p) => (
        <div key={p.repoKind}
             className="flex items-start justify-between gap-4 rounded border border-border p-4">
          <div>
            <p className="font-medium">{p.displayName}</p>
            {/* ⚠ A PROVIDER THAT IS NOT PROVEN SAYS SO, IN ITS OWN WORDS. Offering a button that
                cannot work is worse than offering none — the tenant would reach a dead end and
                have no way to know it was expected. */}
            {!p.available && p.unavailableReason && (
              <p className="mt-1 max-w-prose text-sm text-text-secondary">{p.unavailableReason}</p>
            )}
          </div>
          <Button disabled={!p.available || pending} onClick={() => onAdd(p.repoKind)}>
            {p.available ? 'Connect' : 'Not available yet'}
          </Button>
        </div>
      ))}
    </section>
  );
}

// ── The credential step — the canonical write-only door, as a form ──────────────────────────────

/**
 * ⚠ WRITE-ONLY, AND THE UI SAYS SO RATHER THAN IMPLYING IT. There is no "show" toggle, no masked
 * read-back, and no field pre-filled with anything: once saved, the secret cannot be displayed by
 * this page because the server has no endpoint that returns it. The copy states that plainly —
 * an admin who expects to verify a stored secret later should learn that now, not at the moment
 * they need it.
 *
 * ⚠ THE INPUT IS CLEARED AND THE MUTATION RESET ON SUCCESS. Clearing the field is the visible
 * half; `reset()` is the half that matters, because React Query keeps the submitted variables on
 * the mutation object until it is reset — which would leave the secret reachable in a React
 * devtools inspection for the life of the page.
 */
function CredentialStep({ credential, repoKind, fail, setError }: {
  credential: CredentialState;
  repoKind: string;
  fail: (err: unknown) => void;
  setError: (v: string | null) => void;
}) {
  const supply = useSupplyCredential(repoKind);
  const [values, setValues] = useState<Record<string, string>>({});
  // ⚠ A held credential collapses the form. Re-showing it on demand is what makes "Replace" a
  //   deliberate act rather than a box sitting open next to a working connection.
  const [replacing, setReplacing] = useState(false);
  const step = credential.step;
  const showForm = !credential.has_credential || replacing;
  const complete = step.inputs.every((i) => (values[i.key] ?? '').trim() !== '');

  return (
    <section className="max-w-prose space-y-3 rounded border border-border p-4"
             data-testid="credential-step">
      <h2 className="font-medium">{step.title}</h2>
      <p className="text-sm text-text-secondary">{step.body}</p>

      {credential.has_credential && (
        <div className="space-y-1 rounded bg-surface-2 p-3 text-sm" data-testid="credential-held">
          <p className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-success" />
            {/* ⚠ WHETHER, NEVER WHAT. The server sends no value, so there is none to render. */}
            <span data-testid="credential-status">Credentials saved</span>
          </p>
          <p className="text-text-secondary">{step.suppliedNote}</p>
          {credential.client_id && (
            <p><span className="text-text-secondary">Client ID:</span> {credential.client_id}</p>
          )}
          {credential.supplied_at && (
            <p><span className="text-text-secondary">Saved:</span>{' '}
              {new Date(credential.supplied_at).toLocaleString()}</p>
          )}
          {!replacing && (
            <Button variant="outline" size="sm" data-testid="replace-credential"
                    onClick={() => { setError(null); setReplacing(true); }}>
              Replace credentials
            </Button>
          )}
        </div>
      )}

      {showForm && (
        <div className="space-y-3" data-testid="credential-form">
          {credential.has_credential && (
            <p className="rounded border border-warning/40 bg-warning/5 p-3 text-sm">
              {step.replaceNote}
            </p>
          )}
          {(step.inputs ?? []).map((input) => (
            <div className="space-y-1" key={input.key}>
              <Label htmlFor={`cred-${input.key}`}>{input.label}</Label>
              <Input
                id={`cred-${input.key}`}
                data-testid={`cred-${input.key}`}
                // ⚠ MASKED AND EXCLUDED FROM AUTOFILL because the SERVER said this field is
                //   secret — never because the key happens to be named "secret". A renamed field
                //   must not silently become a plain-text box a browser offers to remember.
                type={input.secret ? 'password' : 'text'}
                autoComplete={input.secret ? 'off' : undefined}
                spellCheck={input.secret ? false : undefined}
                placeholder={input.placeholder}
                value={values[input.key] ?? ''}
                onChange={(e) => setValues((v) => ({ ...v, [input.key]: e.target.value }))}
              />
            </div>
          ))}
          <div className="flex gap-2">
            <Button
              disabled={!complete || supply.isPending}
              data-testid="save-credential"
              onClick={() => {
                setError(null);
                supply.mutate({
                  client_id: values['client_id'] ?? '',
                  enterprise_id: values['enterprise_id'] ?? '',
                  client_secret: values['client_secret'] ?? '',
                }, {
                  onSuccess: () => {
                    // ⚠ BOTH HALVES. The form is cleared, and the mutation is reset so the
                    //   submitted secret stops being reachable on the mutation object.
                    setValues({});
                    setReplacing(false);
                    supply.reset();
                  },
                  onError: fail,
                });
              }}>
              {supply.isPending ? 'Saving…' : step.action}
            </Button>
            {credential.has_credential && replacing && (
              <Button variant="ghost" data-testid="cancel-replace"
                      onClick={() => { setValues({}); setReplacing(false); supply.reset(); }}>
                Cancel
              </Button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

// ── A repository exists: show where the flow is, or what is connected ───────────────────────────

/* eslint-disable @typescript-eslint/no-explicit-any */
function ConnectedOrInFlight({ data, enable, scope, discard, repair, checkHealth, fields, setFields,
  libraryUrl, setLibraryUrl, label, setLabel, fail, setError, deleteIntegration, setNotice }: any) {
  const status = data as OnboardingStatus;
  const b = status.binding!;
  const state = b.onboarding_state;
  const isRetry = state === 'validate_failed';
  // ⚠ P1.2c — GATE 1 IS LEFT ONLY AFTER MICROSOFT CONFIRMS CONSENT. Held here, never derived from a
  //   redirect: it becomes true only from a server "Check consent" answer, and resets the moment the
  //   directory it was checked for is edited.
  const [consentVerified, setConsentVerified] = useState(false);
  const needsConsent = state === 'pending_enable' && status.consent_check === true;

  return (
    <section className="space-y-4">
      <div className="flex items-center gap-2" data-testid="onboarding-state">
        {state === 'connected'
          ? <CheckCircle2 className="h-4 w-4 text-success" />
          : state === 'validating'
            ? <Loader2 className="h-4 w-4 animate-spin text-text-secondary" />
            : <AlertTriangle className="h-4 w-4 text-warning" />}
        <span className="text-sm">Status: {stateLabel(state)}</span>
      </div>

      {/* ⚠ THE LAST PROBE'S OWN WORDS. "Validation failed" teaches an admin nothing and gets
          re-run unchanged; the probe names the ONE thing to go and change. */}
      {isRetry && b.validation_detail && (
        <p className="max-w-prose rounded border border-warning/40 bg-warning/5 p-3 text-sm">
          {b.validation_detail}
        </p>
      )}

      {/* ⚠ BEFORE THE GATE, BECAUSE IT GATES THE GATE. A provider whose app the TENANT owns cannot
          leave Gate 1 without its credential — the server refuses, and putting the form after the
          "continue" button would show the admin a refusal before the thing that satisfies it. */}
      {status.credential && (
        <CredentialStep credential={status.credential} repoKind={b.repo_kind}
                        fail={fail} setError={setError} />
      )}

      {status.gate && (
        <div className="max-w-prose space-y-3 rounded border border-border p-4" data-testid="gate">
          <h2 className="font-medium">{status.gate.title}</h2>
          <p className="text-sm text-text-secondary">{status.gate.body}</p>

          {/* ⚠ THE STEPS COME FIRST (MI UX review): an admin reads the procedure — and who does each
              part — before any field asks them for anything. */}
          {status.gate.script && <GrantScriptSteps steps={status.gate.script.steps} />}

          {/* ⚠ RENDERED ONLY WHEN THE SERVER SUPPLIES ONE. Box has no consent URL and must not get
              a link to a page that does not exist. */}
          {state === 'pending_enable' && status.consent_url && (
            <a href={status.consent_url} target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-1 text-sm text-primary underline">
              Open the approval page <ExternalLink className="h-3 w-3" />
            </a>
          )}

          {/* ⚠ VERIGRC'S APPLICATION ID, SHOWN SO THE ADMIN CAN RUN THE SCRIPT. Without it on the
              page the assisted-setup flow cannot be completed — the script takes -VeriGrcAppId.
              ⓘ Public: it names who is asking; the platform certificate proves it and never
              leaves the server. */}
          {state !== 'pending_enable' && status.platform_app_id && (
            <div className="space-y-1">
              <Label htmlFor="app-id">VeriGRC application ID (for the script)</Label>
              <Input id="app-id" readOnly value={status.platform_app_id}
                     data-testid="platform-app-id"
                     onFocus={(e) => e.currentTarget.select()} />
            </div>
          )}

          {/* ⚠ ASSISTED SETUP: VERIGRC COMPOSES THE COMMAND, THE ADMIN RUNS IT. It is never
              executed here or server-side — the grant is a tenant-admin action performed with
              privileges VeriGRC deliberately does not hold (D-ASSISTED-SETUP-UI-V1). */}
          {status.gate.script && (
            <GrantScriptHelp script={status.gate.script} appId={status.platform_app_id}
                             libraryUrl={libraryUrl} setLibraryUrl={setLibraryUrl} />
          )}

          {/* ⚠ RENDERED FROM THE SERVER'S FIELD LIST, KEYED BY WIRE NAME. Gate 2 asks for TWO ids,
              both pasted from the script's output — VeriGRC cannot resolve them itself. */}
          {(status.gate.inputs ?? []).map((input: { key: string; label: string; placeholder: string }) => (
            <div className="space-y-1" key={input.key}>
              <Label htmlFor={`gate-${input.key}`}>{input.label}</Label>
              <Input id={`gate-${input.key}`}
                     placeholder={input.placeholder}
                     value={fields[input.key] ?? ''}
                     onChange={(e) => {
                       if (input.key === 'directory_id') setConsentVerified(false);
                       setFields((f: Record<string, string>) => ({ ...f, [input.key]: e.target.value }));
                     }} />
            </div>
          ))}

          {needsConsent && (
            <ConsentStep directoryId={fields['directory_id'] ?? ''} onResult={setConsentVerified} />
          )}

          {(state === 'pending_scope' || isRetry) && (
            <div className="space-y-1">
              <Label htmlFor="repo-label">Name for this repository (optional)</Label>
              <Input id="repo-label" value={label} placeholder="Leave blank to use the provider's own name"
                     onChange={(e) => setLabel(e.target.value)} />
            </div>
          )}

          <Button
            disabled={enable.isPending || scope.isPending || (needsConsent && !consentVerified)}
            data-testid="gate-action"
            onClick={() => {
              setError(null);
              if (state === 'pending_enable') {
                enable.mutate({ directory_id: fields['directory_id'] ?? '' }, { onError: fail });
              } else {
                // ⚠ ON A RETRY THE IDS ARE OMITTED — the admin fixed the grant, not the ids, and
                //   the server re-probes what is already on the binding (amendment C).
                scope.mutate({
                  ...(fields['container_ref'] ? { container_ref: fields['container_ref'] } : {}),
                  ...(fields['site_id'] ? { site_id: fields['site_id'] } : {}),
                  ...(label ? { label } : {}),
                }, { onError: fail });
              }
            }}>
            {status.gate.action}
          </Button>
        </div>
      )}

      {state === 'connected' && (
        <div className="max-w-prose space-y-1 rounded border border-border p-4 text-sm"
             data-testid="repository-details">
          {/* ⚠ Box GA S3 (item 10) — SERVED per provider, never a client literal: the same sentence was
              shown to Box tenants, where "only this location" overstates what the probe proves. */}
          {status.access_note && (
            <p className="mb-2 flex items-center gap-2 font-medium" data-testid="repository-access-note">
              <ShieldCheck className="h-4 w-4 text-success" />
              {status.access_note}
            </p>
          )}
          <p><span className="text-text-secondary">{REPOSITORY_LABELS.provider}:</span>{' '}
            {b.repository.provider}</p>
          {/* ⚠ EACH OPTIONAL FIELD IS GATED ON ITSELF. A missing repository name costs its own row
              and nothing else — never the whole block, and never the Open action. */}
          {b.repository.repository && (
            <p><span className="text-text-secondary">{REPOSITORY_LABELS.repository}:</span>{' '}
              {b.repository.repository}</p>
          )}
          {b.repository.container && (
            <p><span className="text-text-secondary">{REPOSITORY_LABELS.container}:</span>{' '}
              {b.repository.container}</p>
          )}
          {b.repository.path && (
            <p><span className="text-text-secondary">{REPOSITORY_LABELS.path}:</span>{' '}
              {b.repository.path}</p>
          )}
          {b.repository.open_url && (
            <a href={b.repository.open_url} target="_blank" rel="noopener noreferrer"
               className="mt-2 inline-flex items-center gap-1 text-primary underline">
              {REPOSITORY_LABELS.open} <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      )}

      {/* ── §C health + §B stability ─────────────────────────────────────────────────────── */}
      {state === 'connected' && status.health && (
        <div className="max-w-prose space-y-2 rounded border border-border p-4 text-sm"
             data-testid="connection-health">
          <p>
            <span className="text-text-secondary">Connection status:</span>{' '}
            <span data-testid="connection-status">
              {status.health.status === 'connected' ? 'Connected' : 'Access issue'}
            </span>
          </p>
          <p className="text-text-secondary">{status.health.detail}</p>
          <p>
            {/* ⚠ WHEN THE PROBE LAST RAN — the honest answer to "is this status current?". A status
                with no timestamp behind it invites the reader to assume it is live. */}
            <span className="text-text-secondary">Last checked:</span>{' '}
            {/* ⓘ Box GA S2 (G2) — Box now runs the same probe, so 'Never checked' means the same for
                every provider: a probe that can run and has not yet. */}
            {status.health_checked_at
              ? new Date(status.health_checked_at).toLocaleString()
              : 'Never checked'}
          </p>
          <Button variant="outline" size="sm" data-testid="check-health"
                  disabled={checkHealth.isPending}
                  onClick={() => { setError(null); checkHealth.mutate(undefined as never, { onError: fail }); }}>
            {checkHealth.isPending ? 'Checking…' : 'Check connection health'}
          </Button>
          <p>
            <span className="text-text-secondary">Last evidence collection:</span>{' '}
            {/* ⚠ NOTHING rather than a misleading recent timestamp. A run that examined no files is
                not a collection, and printing it would say "3 minutes ago" over a surface nobody
                has read in weeks. */}
            {status.last_collection_at
              ? new Date(status.last_collection_at).toLocaleString()
              : 'No evidence collected yet'}
          </p>

          {/* ⚠ REPAIR APPEARS ONLY WHEN THE SERVER SAYS THE CONNECTION IS BROKEN (§B.1). The other
              post-Connected action is Delete Integration (D-CL5-ALLOW-REPO-DISCONNECT, below);
              there is still no edit, disable or enable. */}
          {/* ⚠ Box GA S2 (G9) — THE PANEL'S WORDS AND FIELDS ARE THE SERVER'S, per provider. It was
              SharePoint's grant-script copy hardcoded here and shown for every provider; a Box
              repair re-checks the same folder and asks for nothing. Fields post by WIRE KEY. */}
          {status.repair_available && status.repair && (
            <div className="space-y-2 border-t border-border pt-3" data-testid="repair">
              <p className="font-medium">Repair connection</p>
              <p className="text-text-secondary">{status.repair.body}</p>
              {status.repair.inputs.map((input) => (
                <div className="space-y-1" key={input.key}>
                  <Label htmlFor={`repair-${input.key}`}>{input.label}</Label>
                  <Input id={`repair-${input.key}`} placeholder={input.placeholder}
                         value={fields[`repair_${input.key}`] ?? ''}
                         onChange={(e) => setFields((f: Record<string, string>) => ({
                           ...f, [`repair_${input.key}`]: e.target.value }))} />
                </div>
              ))}
              <Button disabled={repair.isPending
                        || status.repair.inputs.some((i) => i.key === 'container_ref'
                          && !fields['repair_container_ref'])}
                      onClick={() => {
                        setError(null);
                        const vars: { container_ref?: string; site_id?: string } = {};
                        for (const i of status.repair?.inputs ?? []) {
                          const v = fields[`repair_${i.key}`];
                          if (v && (i.key === 'container_ref' || i.key === 'site_id')) vars[i.key] = v;
                        }
                        repair.mutate(vars, { onError: fail });
                      }}>
                {status.repair.action}
              </Button>
            </div>
          )}

          {/* ⚠ SERVER-AUTHORED, PER PROVIDER. It names a console, so it cannot be a client literal:
              hardcoded here it said "Microsoft 365 / Entra" for every provider, which would have
              sent a Box tenant to Microsoft to manage a Box folder (§0/§9). */}
          {status.connected_note && (
            <p className="border-t border-border pt-3 text-text-secondary"
               data-testid="connected-note">
              {status.connected_note}
            </p>
          )}
        </div>
      )}

      {/* ⚠ COLLECTION IS A SEPARATE QUESTION FROM THE CONNECTION, so it is a separate panel and a
          separate query. "VeriGRC can reach this location" and "VeriGRC is reading from it on
          schedule" are answered by different subsystems, and a surface that ran them together would
          let a healthy connection vouch for a sweep that has never completed.

          ⚠ Gated on CONNECTED only -- never on `status.health`. It is its own query, so a page
          whose health block is absent still answers the collection question, and a collection
          outage still leaves the connection details above it readable. */}
      {state === 'connected' && <EvidenceCollectionPanel />}

      {state === 'connected' && <OneAtATime providers={status.providers} currentKind={b.repo_kind} />}

      {/* ⚠ CONNECTED ONLY. A draft is discarded (below), never "deleted" — the server refuses this
          for a draft anyway. The page itself is super_admin-only (RequireRole), as is the route. */}
      {state === 'connected' && (
        <DeleteIntegration
          repository={b.repository}
          note={status.delete_note}
          pending={deleteIntegration.isPending}
          onConfirm={(done) => {
            setError(null);
            deleteIntegration.mutate({ confirmation: 'DELETE' }, {
              onSuccess: () => {
                done();
                setNotice('The integration was deleted. Evidence already captured, control history '
                  + 'and audit records are kept. You can connect a repository below.');
              },
              onError: (err: unknown) => { done(); fail(err); },
            });
          }} />
      )}

      {/* ⚠ DISCARD EXISTS ONLY BEFORE CONNECTED (§A). Nothing is active yet, so no ceremony — but
          it is never offered for a connected repository, and the server refuses one anyway. */}
      {state !== 'connected' && (
        <div className="pt-2">
          <Button variant="ghost" data-testid="discard-setup"
                  disabled={discard.isPending}
                  onClick={() => { setError(null); discard.mutate(undefined as never, { onError: fail }); }}>
            Discard this setup and start over
          </Button>
        </div>
      )}
    </section>
  );
}

// ── Delete the connected integration (D-CL5-ALLOW-REPO-DISCONNECT) ──────────────────────────────

/**
 * MI's message, VERBATIM (D-CL5-ALLOW-REPO-DISCONNECT). ⚠ Asserted word-for-word in the tests —
 * the words are the feature here, so a paraphrase is a defect.
 */
export const DELETE_INTEGRATION_MESSAGE =
  'Deleting this integration removes the connection from VeriGRC. It does not delete your '
  + 'SharePoint or Box data, folders, files, or external application configuration. However, '
  + 'controls that depend on this integration will stop capturing evidence immediately and may '
  + 'significantly affect your control and framework scores. This action cannot be undone.';

/** The one word the admin must type. The server re-checks it; this gate is not the only one. */
const DELETE_WORD = 'DELETE';

/**
 * ⚠ TWO DELIBERATE STEPS, ENDING IN A TYPED WORD. Step 1 names exactly what is being deleted and
 * says what it does and does not do; step 2 will not enable the action until DELETE is typed
 * exactly. Cancel at either step changes nothing.
 */
function DeleteIntegration({ repository, note, pending, onConfirm }: {
  repository: NonNullable<OnboardingStatus['binding']>['repository'];
  /** Box GA S2 (G4) — server-authored, per provider: what deleting does to its credential. */
  note: string | null;
  pending: boolean;
  onConfirm: (done: () => void) => void;
}) {
  const [step, setStep] = useState<0 | 1 | 2>(0);
  const [typed, setTyped] = useState('');
  const close = () => { setStep(0); setTyped(''); };
  const name = repository.repository ?? repository.container ?? repository.path;

  return (
    <div className="max-w-prose space-y-2 rounded border border-danger/30 p-4 text-sm"
         data-testid="delete-integration-section">
      <p className="font-medium">Delete integration</p>
      <p className="text-text-secondary">
        Removes this repository&apos;s connection from VeriGRC so a different repository can be
        connected. Evidence already captured is kept.
      </p>
      <Button variant="outline" size="sm" data-testid="delete-integration"
              className="border-danger/40 text-danger hover:bg-danger/5"
              onClick={() => setStep(1)}>
        Delete Integration
      </Button>

      <Dialog open={step > 0} onOpenChange={(open) => { if (!open) close(); }}>
        <DialogContent data-testid="delete-integration-dialog">
          <DialogHeader>
            <DialogTitle>Delete this integration?</DialogTitle>
            <DialogDescription data-testid="delete-integration-target">
              {repository.provider}{name ? ` — ${name}` : ''}
            </DialogDescription>
          </DialogHeader>

          <p className="text-sm" data-testid="delete-integration-message">{DELETE_INTEGRATION_MESSAGE}</p>
          {/* ⓘ Box GA S2 (G4) — beside, never inside, MI's verbatim message. */}
          {note && (
            <p className="text-sm" data-testid="delete-integration-note">{note}</p>
          )}
          <p className="text-sm font-medium" data-testid="delete-integration-effect">
            Controls that depend on this integration stop capturing evidence immediately.
          </p>

          {step === 1 && (
            <DialogFooter>
              <Button variant="ghost" onClick={close}>Cancel</Button>
              <Button variant="destructive" data-testid="delete-integration-continue"
                      onClick={() => setStep(2)}>
                Continue
              </Button>
            </DialogFooter>
          )}

          {step === 2 && (
            <div className="space-y-3">
              <div className="space-y-1">
                <Label htmlFor="delete-confirm">Type {DELETE_WORD} to confirm</Label>
                <Input id="delete-confirm" data-testid="delete-confirm-input" autoComplete="off"
                       spellCheck={false} value={typed} onChange={(e) => setTyped(e.target.value)} />
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={close}>Cancel</Button>
                {/* ⚠ EXACTLY the word — not trimmed, not case-folded. */}
                <Button variant="destructive" data-testid="delete-integration-confirm"
                        disabled={typed !== DELETE_WORD || pending}
                        onClick={() => onConfirm(close)}>
                  {pending ? 'Deleting…' : 'Delete integration'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * ⚠ ONE REPOSITORY AT A TIME, SAID OUT LOUD. While one integration is connected the other provider
 * cannot be added (uq_cts_eb_one_live_per_tenant); the page shows that rather than hiding the
 * option. ⓘ Text, not a disabled button — there is nothing to press until the current one is gone.
 */
function OneAtATime({ providers, currentKind }: {
  providers: OnboardingStatus['providers']; currentKind: string;
}) {
  const others = (providers ?? []).filter((p) => p.repoKind !== currentKind);
  if (others.length === 0) return null;
  return (
    <div className="max-w-prose space-y-2 rounded border border-border p-4 text-sm"
         data-testid="one-at-a-time">
      <p className="font-medium">Other repository types</p>
      {others.map((p) => (
        <p key={p.repoKind} data-testid={`unavailable-${p.repoKind}`}>
          <span className="font-medium">{p.displayName}</span>{' '}
          <span className="text-text-secondary">
            — unavailable while this repository is connected. VeriGRC uses one evidence repository
            at a time; delete the current integration to connect a different one.
          </span>
        </p>
      ))}
    </div>
  );
}
// ── Gate 2 — the grant script: steps, download, and the admin's own command (S-GRANT-DELIVERY) ──

/** Who performs a step, as the admin reads it. */
const STEP_ACTOR: Record<'admin' | 'verigrc', string> = {
  admin: 'Authorized SharePoint administrator',
  verigrc: 'VeriGRC',
};

/** The procedure, numbered, each step tagged with who performs it. Rendered first in the gate. */
function GrantScriptSteps({ steps }: { steps: GateScript['steps'] }) {
  return (
    <ol className="space-y-2 text-sm" data-testid="script-steps">
      {(steps ?? []).map((s, i) => (
        <li key={i} className="flex gap-2" data-testid={`script-step-${i + 1}`}>
          <span className="shrink-0 font-medium">{i + 1}.</span>
          <span>
            <span className={`mr-2 inline-block rounded px-1.5 py-0.5 text-xs font-medium ${
              s.by === 'verigrc' ? 'bg-primary/10 text-primary' : 'bg-surface-2 text-text-primary'}`}
                  data-testid="step-actor">
              {STEP_ACTOR[s.by] ?? s.by}
            </span>
            {s.text}
          </span>
        </li>
      ))}
    </ol>
  );
}

/**
 * ⚠ THE SCRIPT IS DELIVERED BY THE PRODUCT, AND ITS HASH IS THE BUNDLE'S OWN. The download is the
 * file the build emitted (grant-script.plugin.ts) and the SHA-256 is computed from those same bytes
 * at build time — so what an admin verifies is what was served, with no literal in between.
 *
 * ⚠ THE COMMAND IS THE SERVER'S TEMPLATE with the same two substitutions, computed once and used for
 * BOTH the visible box and the Copy control, so what is copied is exactly what is shown.
 *
 * ⓘ IN THE ORDER AN ADMINISTRATOR WORKS (D-EV-GRANT-PAGE-MANAGED-WINDOWS-WORKFLOW): the library address,
 * then the download with the two commands that locate and verify it, then the generated command.
 * No hash is written into this copy — the one shown is computed from the bytes served.
 */
/** PowerShell, for the default browser download folder. A fixed literal — nothing is substituted. */
const CD_DOWNLOADS = 'Set-Location "$env:USERPROFILE\\Downloads"';
function GrantScriptHelp({ script, appId, libraryUrl, setLibraryUrl }: {
  script: GateScript;
  appId: string | null;
  libraryUrl: string;
  setLibraryUrl: (v: string) => void;
}) {
  const [copy, setCopy] = useState<'idle' | 'copied' | 'failed'>('idle');
  // ⚠ Only a template that names THIS script is offered THIS download — a future provider's script
  //   must never be paired with the SharePoint grant script by accident.
  const offersDownload = script.template.includes(GRANT_SCRIPT_FILENAME);
  // ⚠ Shown only once BOTH parts are known — a half-filled command an admin might paste anyway is
  //   worse than none.
  // ⚠ SCR item 3: the address is VALIDATED before any command exists, and substituted LITERALLY
  //   (src/lib/grant-command.ts). A refused address yields no command and nothing to copy.
  const addressCheck = libraryUrl ? checkLibraryAddress(libraryUrl) : null;
  const command = libraryUrl && appId ? buildGrantCommand(script.template, libraryUrl, appId) : null;

  return (
    <div className="space-y-3" data-testid="script-help">
      <div className="space-y-1">
        <Label htmlFor="library-url">{script.urlLabel}</Label>
        <Input id="library-url"
               placeholder={script.urlPlaceholder}
               value={libraryUrl}
               aria-invalid={addressCheck?.ok === false ? true : undefined}
               onChange={(e) => { setCopy('idle'); setLibraryUrl(e.target.value); }} />
        {addressCheck && !addressCheck.ok && (
          <p role="alert" className="text-xs text-danger" data-testid="library-url-error">
            {addressCheck.reason}
          </p>
        )}
      </div>

      {offersDownload && (
        <div className="space-y-2 rounded border border-border p-3 text-sm" data-testid="grant-script-download">
          <a href={`${import.meta.env.BASE_URL}${GRANT_SCRIPT_ASSET_PATH}`} download={GRANT_SCRIPT_FILENAME}
             className="inline-flex items-center gap-1 font-medium text-primary underline">
            <Download className="h-4 w-4" /> Download {GRANT_SCRIPT_FILENAME}
          </a>
          <p className="text-text-secondary">
            SHA-256:{' '}
            <code className="break-all font-mono text-xs" data-testid="grant-script-sha256">
              {GRANT_SCRIPT_SHA256}
            </code>
          </p>
          <p className="text-xs text-text-secondary">
            In PowerShell 7, change to the folder that holds the download. For your Downloads folder:
          </p>
          <code className="block break-all font-mono text-xs" data-testid="grant-cd-command">{CD_DOWNLOADS}</code>
          <p className="text-xs text-text-secondary">
            Then verify the file. The result must exactly match the SHA-256 shown above — do not run the
            script if the values differ:
          </p>
          <code className="block break-all font-mono text-xs" data-testid="grant-hash-command">
            Get-FileHash .\{GRANT_SCRIPT_FILENAME} -Algorithm SHA256
          </code>
        </div>
      )}

      {command && (
        <div className="space-y-2">
          <p className="text-sm" data-testid="command-instructions">
            Copy and run the complete command below as a single line in PowerShell 7.
          </p>
          {/* ⚠ WRAPPED, NOT SCROLLED: the whole command is visible, including the app ID at its end. It is
              ONE line — the wrapping is only how it is displayed. */}
          <pre data-testid="script-command"
               className="whitespace-pre-wrap break-all rounded border border-border bg-surface-2 p-3 text-xs">
            {command}
          </pre>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" size="sm" aria-label="Copy command"
                    data-testid="copy-command"
                    onClick={() => {
                      navigator.clipboard.writeText(command)
                        .then(() => setCopy('copied'), () => setCopy('failed'));
                    }}>
              <Copy className="mr-1 h-4 w-4" /> Copy command
            </Button>
            <span role="status" className="text-xs text-text-secondary" data-testid="copy-status">
              {copy === 'copied' ? 'Copied.'
                : copy === 'failed' ? 'Could not copy — select the command above and copy it yourself.'
                  : ''}
            </span>
          </div>
          {/* ⚠ RULING B: the bypass is the started process's only — never the machine's or the user's. */}
          <p className="text-xs text-text-secondary" data-testid="exec-policy-note">
            <code className="font-mono">-ExecutionPolicy Bypass</code> applies only to the new PowerShell
            process this command starts. It does not change the machine-wide or user execution policy. If
            your organization does not permit a process-level bypass, stop and contact your IT or security
            administrator — do not weaken or permanently change the device&apos;s execution policy.
          </p>
        </div>
      )}
    </div>
  );
}
